import { resolveEmailSender } from "@ando/ui";
import { refineField } from "@ando/core";
import {
  type ActionErrorKind,
  ActionErrorRecovery,
  ActionFieldEditor,
  type ActionFieldSpec,
  type ActionFieldValue,
  ActionProposalSurface,
  ActionReceipt,
  emailOwnedFields,
  EmailSendDraftPreview,
  type RefineSubmitInput,
  type RefineSubmitWithDiff,
  sanitizeHtml,
  seedActionValues,
  toast,
  validateField,
} from "@ando/ui";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";

import {
  useEffectiveSignature,
  useSignatures,
} from "../../sales/hooks/useEmailTemplates";
import { useBrandFonts } from "../../settings/hooks/useBrandFonts";
import {
  useLoadPeopleOptions,
  useSearchAttachableDocuments,
  useUploadAttachment,
} from "../hooks/useActionEditorTransports";
import { activeActionApi } from "../hooks/useActiveAction";
import { useConfirmWithUndo } from "../hooks/useConfirmWithUndo";
import { usePayloadSchema } from "../hooks/usePayloadSchema";
import type { ActiveAction } from "../state/activeActionStore";
import { renderActionTypeIcon } from "../utils/actionTypeIcon";
import { readSourceValues } from "../utils/gateSourceValues";

/**
 * ScalarActionProposalMount — apps-layer container that makes a SCALAR
 * (non-document) action proposal TAKE OVER the work area through the same
 * generalised ``ActionProposalSurface`` shell the presentation deck uses
 * (Wave 9). The proposal is no longer a small disconnected banner the user
 * clicks into: its editable surface + the in-surface Confirm/Reject bar are
 * ONE thing, consistent with every other action type.
 *
 * Reuse, not rebuild:
 *   - the editable BODY is the EXISTING per-type editor — the Gmail-style
 *     ``EmailSendDraftPreview`` envelope for ``send_email``, the shared
 *     schema-driven ``ActionFieldEditor`` for everything else. We do NOT
 *     fork a new composer.
 *   - the schema comes from ``usePayloadSchema`` (wire-shipped wins when
 *     present), exactly as ``FocusedActionView`` resolves it.
 *   - Confirm runs through the SAME shared ``useProposalExecution``
 *     lifecycle (``executing → executed | proposed`` patch dance) as
 *     ``SlideDeckProposalMount`` / ``FocusedActionView`` — so the shell's
 *     status badge and decision bar behave identically across action types.
 *
 * ── Extension point ──────────────────────────────────────────────────────
 * ``renderBody`` is the ONLY action-type-specific seam. To route a new
 * action type into the work-area surface, add a case here (or fall through
 * to the generic ``ActionFieldEditor``) — the toolbar / decision / status
 * chrome is inherited unchanged. Remaining scalar types still served by the
 * legacy ``FocusedActionView`` overlay (calendar_create_event,
 * fic_create_invoice / fic_credit_note / fic_payment, aruba_pec_send /
 * infocert_pec_send, submit_invoice_sdi, send_message, salesforce_* /
 * hubspot_* / sap_* / acube_* generic) can be migrated case-by-case the
 * same way — they already have vendor previews in ``FocusedActionView``'s
 * ``pickPreview`` that accept the same controlled ctx.
 */

/**
 * Action types this mount owns today. Anything NOT in here keeps the
 * existing ``FocusedActionView`` overlay — the migration is opt-in per
 * type so no existing flow half-breaks.
 */
const SCALAR_SURFACE_ACTION_TYPES = new Set<string>(["send_email", "email_send_draft"]);

export function isScalarSurfaceActionType(actionType: string): boolean {
  return SCALAR_SURFACE_ACTION_TYPES.has(actionType);
}

export interface ScalarActionProposalMountProps {
  readonly action: ActiveAction;
  /** Fired after reject — the route clears ``?proposal=`` and returns home. */
  readonly onAfterReject?: () => void;
}

export const ScalarActionProposalMount: React.FC<ScalarActionProposalMountProps> = ({
  action,
  onAfterReject,
}) => {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { confirmWithUndo, cancel, pending, busy: executing } = useConfirmWithUndo();
  const payloadSchema = usePayloadSchema(action.id);

  // Wire-shipped schema wins (preview-synthesised ``send_email`` ships a
  // lite version on the WS frame); otherwise the lazily-fetched unified
  // schema — identical resolution to ``FocusedActionView``.
  const schema: readonly ActionFieldSpec[] =
    action.schema.length > 0 ? action.schema : payloadSchema.editorSchema;

  // Controlled value map — proposed merged with any saved edits. Reset when
  // the proposal id (or the resolved schema) changes so a freshly-arrived
  // proposal doesn't inherit a stale map.
  // Seed layers schema-resolved defaults (``spec.value``, e.g. an auto-selected
  // account) under ``proposedFields`` under the user's edits — so an auto-filled
  // required field isn't reported empty. Shared helper (DRY, see actionFieldSeed).
  const seed = React.useMemo<Record<string, ActionFieldValue>>(
    () => seedActionValues(schema, action.proposedFields, action.editedFields),
    [schema, action.proposedFields, action.editedFields],
  );
  const [values, setValues] = React.useState<Record<string, ActionFieldValue>>(seed);
  React.useEffect(() => {
    setValues(seed);
  }, [seed]);

  // Ref-stable transports — see ``useActionEditorTransports`` for the why.
  const loadPeopleOptions = useLoadPeopleOptions();
  const uploadAttachment = useUploadAttachment();
  const searchAttachableDocuments = useSearchAttachableDocuments();

  const errors = React.useMemo(() => {
    const out: Record<string, { message: string } | undefined> = {};
    for (const spec of schema) {
      out[spec.name] = validateField(spec, values[spec.name] ?? null, i18n);
    }
    return out;
  }, [schema, values, i18n]);
  const hasErrors = React.useMemo(
    () => Object.values(errors).some((e) => e !== undefined),
    [errors],
  );

  // The undo window locks editing too — the user already committed; ``pending``
  // is the "sto per inviare" state.
  const busy = executing || action.status === "executing" || pending !== null;

  // Single shared inline-AI-refiner transport — same wiring as
  // ``FocusedActionView`` so the email body keeps its selection bubble.
  const refineSubmit = React.useCallback(async (input: RefineSubmitInput) => {
    const res = await refineField(input.proposalId, {
      field_name: input.fieldName,
      full_text: input.fullText,
      selection_start: input.selectionStart,
      selection_end: input.selectionEnd,
      instruction: input.instruction,
    });
    return {
      success: res.success,
      newFullText: res.new_full_text ?? null,
      message: res.message ?? null,
    };
  }, []);

  // Diff-mode transport (Jul 2026) — same audited refine-field call, but
  // returns the SELECTED substring before/after + word-level ``changes`` so the
  // body's InlineAIRefiner reviews the rewrite as an Apple-style inline redline
  // before committing. ``original`` = selected substring → the primitive splices
  // the reconstructed text back over the selection.
  const refineSubmitWithDiff = React.useCallback<RefineSubmitWithDiff>(
    async (input) => {
      const res = await refineField(input.proposalId, {
        field_name: input.fieldName,
        full_text: input.fullText,
        selection_start: input.selectionStart,
        selection_end: input.selectionEnd,
        instruction: input.instruction,
      });
      if (!res.success) {
        throw new Error(res.message ?? t`Impossibile rigenerare la selezione.`);
      }
      const selected = input.fullText.slice(input.selectionStart, input.selectionEnd);
      return {
        original: res.original ?? selected,
        rewritten: res.new_text ?? selected,
        changes: res.changes ?? [],
      };
    },
    [t],
  );

  // Single confirm implementation. ``extraModifications`` seed the map before
  // the diff (e.g. { confirm_source: true } from the Tier-2 gate recovery), so
  // the hasErrors guard, audit diff, undo window + toasts stay in ONE place.
  const submit = React.useCallback(
    (extraModifications?: Record<string, unknown>) => {
      if (hasErrors) {
        toast.error(t`Correggi i campi evidenziati prima di confermare`);
        return;
      }
      // Diff vs proposed — only changed keys travel as modifications (same
      // audit contract as FocusedActionView).
      const proposed = action.proposedFields;
      const modifications: Record<string, unknown> = { ...extraModifications };
      for (const [k, v] of Object.entries(values)) {
        if (proposed[k] !== v) modifications[k] = v;
      }
      // Confirm defers the real execute by the undo window (Feature 2). The
      // generic executing→executed|error patch lifecycle (+ persisting
      // ``editedFields``, receipt stash, error-kind classification) lives in
      // ``useProposalExecution`` under the hood. Classified failures render an
      // INLINE recovery (Feature 4), so we no longer toast on error — only on
      // the undo cancel + the success confirmation.
      confirmWithUndo(action.id, Object.keys(modifications).length > 0 ? modifications : undefined, {
        editedFields: values,
        onUndo: () => {
          toast.info(t`Invio annullato`);
        },
        onSuccess: () => {
          toast.success(t`Azione eseguita`, {
            description: typeof action.title === "string" ? action.title : undefined,
          });
        },
      });
    },
    [action.id, action.proposedFields, action.title, confirmWithUndo, hasErrors, t, values],
  );

  // Ignores any event arg (used directly as onRetry/onConfirm).
  const handleConfirm = React.useCallback(() => submit(), [submit]);

  // Tier-2 gate recovery: re-execute confirming the value against the source of
  // record. The BE reads ``modifications.confirm_source`` and lets an *unattested*
  // value through (a *contradicted* one stays blocked regardless).
  const handleConfirmSource = React.useCallback(() => submit({ confirm_source: true }), [submit]);

  const handleReject = React.useCallback(() => {
    // Esc / Rifiuta also cancels a pending send before removing the card.
    cancel();
    activeActionApi.remove(action.id);
    onAfterReject?.();
  }, [action.id, cancel, onAfterReject]);

  const handleReconnect = React.useCallback(() => {
    void navigate({ to: "/integrations" });
  }, [navigate]);

  // ── Feature 3: post-execute RECEIPT ─────────────────────────────────────
  const receiptNode = React.useMemo(() => {
    if (action.status !== "executed") return undefined;
    const data = (action.executedData ?? {}) as {
      result_url?: unknown;
      result_label?: unknown;
      external_id?: unknown;
    };
    const url = typeof data.result_url === "string" ? data.result_url : null;
    const externalId = typeof data.external_id === "string" ? data.external_id : null;
    if (!url && !externalId) return undefined;
    return (
      <ActionReceipt
        url={url}
        label={typeof data.result_label === "string" ? data.result_label : null}
        externalId={externalId}
      />
    );
  }, [action.status, action.executedData]);

  // ── Feature 4: inline error recovery ────────────────────────────────────
  const errorNode = React.useMemo(() => {
    if (!action.errorKind) return undefined;
    if (action.status === "executing" || action.status === "executed") return undefined;
    const meta = (action.errorMeta ?? {}) as { account_label?: unknown };
    const sourceValues = readSourceValues(action.errorMeta);
    return (
      <ActionErrorRecovery
        kind={action.errorKind as ActionErrorKind}
        message={action.error ?? undefined}
        accountLabel={typeof meta.account_label === "string" ? meta.account_label : undefined}
        sourceValues={sourceValues}
        busy={busy}
        onRetry={handleConfirm}
        onReconnect={handleReconnect}
        onConfirmSource={handleConfirmSource}
      />
    );
  }, [
    action.errorKind,
    action.errorMeta,
    action.error,
    action.status,
    busy,
    handleConfirm,
    handleReconnect,
    handleConfirmSource,
  ]);

  // ── Feature 2: pending undo window → surface prop ───────────────────────
  const pendingProp = React.useMemo(
    () =>
      pending
        ? {
            secondsLeft: pending.secondsLeft,
            totalSeconds: pending.totalSeconds,
            onCancel: cancel,
            // This mount only serves send_email / email_send_draft today.
            actionNoun: t`Invio`,
          }
        : undefined,
    [pending, cancel, t],
  );

  // ── Body picker — the ONLY action-type-specific seam (extension point) ──
  // Firma — resolved for the CURRENTLY SELECTED "Da" mailbox so changing the
  // from-account re-resolves the signature (per-account default → user → org).
  // ``values.from_account`` is the account picked in the envelope's Da picker.
  const fromAccount =
    typeof values.from_account === "string" && values.from_account
      ? values.from_account
      : undefined;
  const sender = resolveEmailSender(schema.find((field) => field.name === "from_account")?.options ?? [], fromAccount);
  const effectiveSig = useEffectiveSignature(sender.realEmail, !sender.sendOnly && (!fromAccount || Boolean(sender.realEmail)));
  // Pool of named signatures the operator can pick between for THIS email.
  const signaturePool = useSignatures();
  const signatureOptions = React.useMemo(
    () => (signaturePool.data ?? []).map((s) => ({ id: s.id, name: s.name })),
    [signaturePool.data],
  );
  // Manual pick overrides the account-resolved default; reset it whenever the
  // from-account changes so the new mailbox's default wins (matches "changing
  // Da re-resolves the signature").
  const [pickedSignatureId, setPickedSignatureId] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    setPickedSignatureId(null);
  }, [fromAccount]);
  const activeSignatureId =
    pickedSignatureId ?? effectiveSig.data?.signature_id ?? null;
  const signatureHtml = React.useMemo(() => {
    const picked =
      pickedSignatureId != null
        ? signaturePool.data?.find((s) => s.id === pickedSignatureId)
        : undefined;
    return sanitizeHtml(
      picked?.signature_html ?? effectiveSig.data?.effective_html ?? "",
    );
  }, [pickedSignatureId, signaturePool.data, effectiveSig.data?.effective_html]);
  // Brand Kit fonts for the email body editor's font dropdown (+ FontFace load).
  const brandFonts = useBrandFonts();

  const isEmail = action.actionType === "send_email" || action.actionType === "email_send_draft";
  // Include the live ``type:"file"`` field (``attachments``) so the stacked
  // editor never renders a SECOND "Allegati" section below — attachments live
  // INSIDE the envelope sheet (identical to the inbox reply).
  const ownedSet = isEmail ? new Set<string>(emailOwnedFields(schema)) : null;
  // The stacked ``ActionFieldEditor`` skips fields the envelope owns inline
  // (mirrors ActionDrawer.previewOwnedFields) so no input renders twice.
  const stackedSchema = (ownedSet ? schema.filter((s) => !ownedSet.has(s.name)) : schema).map((field) => sender.sendOnly && ["scheduled_for", "send_at", "provider", "in_reply_to_message_id", "reply_to_message_id", "forward_message_id", "thread_id"].includes(field.name) ? { ...field, readonly: true } : field);

  const body = (
    <div className="space-y-4 px-4 py-4">
      {isEmail ? (
        <EmailSendDraftPreview
          proposalId={action.id}
          refineSubmit={refineSubmit}
          refineSubmitWithDiff={refineSubmitWithDiff}
          proposedFields={action.proposedFields}
          executedFields={action.editedFields}
          schema={schema}
          values={values}
          errors={errors}
          onChange={setValues}
          disabled={busy}
          // Same ref-stable transport as the stacked ``ActionFieldEditor``
          // below so the envelope ``A`` / ``Cc`` / ``Ccn`` autocomplete is
          // driven by ``GET /people/search`` scoped to the chosen sender.
          loadPeopleOptions={loadPeopleOptions}
          uploadAttachment={uploadAttachment}
          searchAttachableDocuments={searchAttachableDocuments}
          brandFonts={brandFonts}
          signatureHtml={sender.sendOnly ? undefined : signatureHtml || undefined}
          signatures={sender.sendOnly ? [] : signatureOptions}
          activeSignatureId={activeSignatureId}
          onPickSignature={setPickedSignatureId}
        />
      ) : null}
      {stackedSchema.length > 0 ? (
        <ActionFieldEditor
          schema={stackedSchema}
          values={values}
          errors={errors}
          onChange={setValues}
          disabled={busy}
          // ``recipients`` fields autocomplete via the chosen account's
          // people-API (Google People + MS Graph). ``file`` fields get
          // the shared upload transport. Both transports are ref-stable
          // (see ``useActionEditorTransports``) so the editor's debounce
          // ``useEffect`` doesn't re-arm on every parent re-render.
          loadPeopleOptions={loadPeopleOptions}
          uploadAttachment={uploadAttachment}
          searchAttachableDocuments={searchAttachableDocuments}
        />
      ) : null}
    </div>
  );

  return (
    <ActionProposalSurface
      icon={renderActionTypeIcon(action.actionType)}
      title={action.title}
      rationale={action.subtitle ?? undefined}
      status={action.status}
      busy={busy}
      confirmDisabled={hasErrors}
      onConfirm={handleConfirm}
      onReject={handleReject}
      body={body}
      pending={pendingProp}
      receipt={receiptNode}
      errorBanner={errorNode}
    />
  );
};
ScalarActionProposalMount.displayName = "ScalarActionProposalMount";
